import {
  useEffect,
  useState,
  useMemo,
  memo
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
  Card,
  Divider,
  alpha,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router';
import { getSelectedSiteFilterValue, getStateName } from 'src/utils/getInitials';
import { useDispatch, useSelector } from 'react-redux';
import {
  getRequestorList,
} from 'src/redux/requestorSlice';
import Loader from 'src/common/Loader';
import ROUTE from 'src/utils/routeConstants';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import { getAlignByColumnId, userTableHeaders } from 'src/utils/tableHeaders';
import CustomPagination from 'src/common/CustomPagination';
import {
  getTableActionIconButtonSx,
  TABLE_ACTION_ICON_SX
} from 'src/common/tableActionStyles';

/* -------- Modern Status Pill -------- */

const StatusPill = ({ active }) => (
  <Chip
    label={active ? 'Active' : 'Inactive'}
    color={active ? 'success' : 'error'}
    size="small"
    variant="outlined"
    sx={{ fontWeight: 600 }}
  />
);

StatusPill.propTypes = {
  active: PropTypes.bool
};

/* -------- Mobile Card View -------- */

const MobileCardView = memo(({ user, onViewClick }) => (
  <Card
    sx={{
      mb: 2,
      p: 2,
      borderRadius: 2,
      boxShadow: 'none',
      border: '1px solid',
      borderColor: 'divider'
    }}
  >
    <Stack direction="row" justifyContent="space-between" mb={2}>
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {`${user.first_name} ${user.last_name}`}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {user.emp_id}
        </Typography>
      </Box>
      <StatusPill active={user.is_active} />
    </Stack>

    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <LocationIcon sx={{ color: 'text.disabled', fontSize: 16 }} />
      <Typography variant="body2" color="text.secondary">
        {`${user.city}, ${getStateName(user.state)}`}
      </Typography>
    </Stack>

    <Box textAlign="right">
      <IconButton onClick={() => onViewClick(user.id)}>
        <ChevronRightIcon />
      </IconButton>
    </Box>
  </Card>
));

MobileCardView.propTypes = {
  user: PropTypes.object.isRequired,
  onViewClick: PropTypes.func.isRequired
};

/* -------- Main Table -------- */

const RequestorListTable = ({
  refreshData, searchText, searchFilters, siteFilter
}) => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();

  const { loading, requestorList, totalCount } = useSelector(
    (state) => state.requestors
  );

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [limit, setLimit] = useState(25);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('first_name');
  const userAlignById = useMemo(() => getAlignByColumnId(userTableHeaders), []);
  const selectedSite = getSelectedSiteFilterValue(siteFilter);

  useEffect(() => {
    setPage(0);
    dispatch(
      getRequestorList({
        limit,
        pageNo: 1,
        params: { ...searchFilters, search: searchText, site: selectedSite }
      })
    );
  }, [dispatch, limit, refreshData, searchFilters, searchText, selectedSite]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    dispatch(
      getRequestorList({
        limit,
        pageNo: newPage + 1,
        params: { ...searchFilters, search: searchText, site: selectedSite }
      })
    );
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
  };

  const viewDetails = (id) => {
    history.push(`${ROUTE.requestors}/${id}`);
  };

  const handleRequestSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const displayData = requestorList
    ?.slice()
    .sort(getComparator(order, orderBy)) || [];

  return loading ? (
    <Loader />
  ) : (
    <Box sx={{ height: '100%' }}>
      {isMobile ? (
        <>
          {displayData.map((user) => (
            <MobileCardView
              key={user.id}
              user={user}
              onViewClick={viewDetails}
            />
          ))}
          <CustomPagination
            page={page}
            rowsPerPage={limit}
            count={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            showRowsPerPage
          />
        </>
      ) : (
        <Box
          sx={{
            width: '100%',
            borderRadius: 2,
            bgcolor: '#FFFFFF',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              minHeight: 0,
              flex: 1,
              overflow: 'auto',
              '& .MuiTableCell-stickyHeader': {
                backgroundColor: '#F9FAFB',
                borderBottom: `1px solid ${theme.palette.divider}`,
                zIndex: 2
              },
              '& .MuiTableHead-root .MuiTableCell-root:last-of-type': {
                position: 'sticky',
                right: 0,
                zIndex: 3,
                backgroundColor: '#F9FAFB',
                boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
              }
            }}
          >
            <Box sx={{ minWidth: { xs: 760, sm: 900, md: 1050 } }}>
              <Table stickyHeader>
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  headCells={userTableHeaders}
                  onRequestSort={handleRequestSort}
                  sx={{
                    backgroundColor: '#F9FAFB',
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}
                />
                <TableBody>
                  {displayData.length > 0 ? (
                    displayData.map((user) => (
                      <TableRow
                        hover
                        key={user.id}
                        sx={{
                          borderBottom: `1px solid ${alpha(
                            theme.palette.divider,
                            0.5
                          )}`,
                          '&:hover': {
                            backgroundColor: '#F9FAFB'
                          }
                        }}
                      >
                        <TableCell align={userAlignById.first_name}>
                          <Box
                            sx={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: userAlignById.first_name === 'center' ? 'center' : 'flex-start'
                            }}
                          >
                            <Typography>
                              {`${user.first_name} ${user.last_name}`}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell align={userAlignById.emp_id}>
                          {user.emp_id}
                        </TableCell>

                        <TableCell align={userAlignById.mobile_num}>
                          {user.mobile_num}
                        </TableCell>

                        <TableCell align={userAlignById.city}>
                          {`${user.city}, ${getStateName(user.state)}`}
                        </TableCell>

                        <TableCell align={userAlignById.timezone}>
                          {user.timezone || 'Asia/Kolkata'}
                        </TableCell>

                        <TableCell align={userAlignById.is_active}>
                          <StatusPill active={user.is_active} />
                        </TableCell>

                        <TableCell
                          align={userAlignById.action}
                          sx={{
                            position: 'sticky',
                            right: 0,
                            zIndex: 1,
                            backgroundColor: '#FFFFFF',
                            boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
                          }}
                        >
                          <Tooltip title="View Details">
                            <IconButton
                              onClick={() => viewDetails(user.id)}
                              size="medium"
                              aria-label="View details"
                              sx={getTableActionIconButtonSx(theme)}
                            >
                              <ChevronRightIcon sx={TABLE_ACTION_ICON_SX} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={userTableHeaders.length} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">
                          No Requestors Found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>

          <Divider sx={{ flexShrink: 0 }} />

          <Box sx={{ p: 1, flexShrink: 0, bgcolor: '#FFFFFF' }}>
            <CustomPagination
              page={page}
              rowsPerPage={limit}
              count={totalCount}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleLimitChange}
              showRowsPerPage
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

RequestorListTable.propTypes = {
  refreshData: PropTypes.bool,
  searchText: PropTypes.string,
  searchFilters: PropTypes.object,
  siteFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default RequestorListTable;
